import { computed, onMounted } from 'vue'
import store from '@/store'
import i18n from '@/i18n'
import { RestApiClient } from 'ifield-core-components'
import { FUNCTION_TYPE } from '@/constants'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { useInheritLayerVisible } from '@/composables/UseInheritLayerVisible'
import { useStoreListSetting } from '@/composables/UseStoreListSetting'

/**
 * ログイン状態であるときの初期化処理
 */
export function useInitializeAtLoggedIn() {
  const { handleApiError } = useHandleApiError()
  const { init: initInheritLayerVisibleClient, inheritLayerVisible } =
    useInheritLayerVisible()
  const { restoreListSettingFromLocalStorage } = useStoreListSetting()

  /**
   * サーバからデータを取得して更新する処理のラッパー
   * @param {function.<Promise>} updater 実際の更新処理
   * @param {boolean} isInLoginSequence ログイン処理の一連でcallしている場合はtrue
   */
  const updateData = async (updater, isInLoginSequence = false) => {
    try {
      await updater()
    } catch (error) {
      if (isInLoginSequence) {
        await handleApiError(error, _onApiFailure, true)
      } else {
        await handleApiError(error)
      }
    }
  }

  /**
   * 取得失敗時の処理
   * @param {Response|Error} error APIエラー
   */
  const _onApiFailure = async (error) => {
    let message

    if (error instanceof Response) {
      let json = await error.json()
      if (json.status === 'FAILURE' && json.message) {
        message = json.message
      } else {
        message = i18n.t('useInitializeAtLoggedIn.message.serverError')
      }
    } else {
      message = error
    }

    throw new Error(message)
  }

  /**
   * 屋内権限が利用可能な場合はtrue
   * @type {ComputedRef.<boolean>}
   */
  const isIndoorAvailable = computed(() =>
    store.getters['functionType'](FUNCTION_TYPE.INDOOR)
  )

  /**
   * ログインされている状態でロードされた時に実行する処理
   * @param {boolean} isInLoginSequence ログイン処理の一連でcallしている場合はtrue
   */
  const onLoadAtLoggedIn = async (isInLoginSequence) => {
    const tasks = [
      store.dispatch('checkinMarker/initializeState'),
      store.dispatch('reportMarker/initializeState'),
      store.dispatch('userMarker/initializeState'),
      // ユーザーロール
      updateData(() => store.dispatch('role/updateRoles'), isInLoginSequence),
      // 文字列テンプレート
      updateData(
        () => store.dispatch('textTemplate/updateTextTemplates'),
        isInLoginSequence
      ),
      // マップレイヤー
      updateData(
        () => store.dispatch('mapLayer/updateMapLayersWithThrottle'),
        isInLoginSequence
      ),
      // ユーザーリスト表示項目
      updateData(
        () =>
          store.dispatch(
            'systemSetting/updateSettingByItemKey',
            'user_disp_item'
          ),
        isInLoginSequence
      ),
      // ユーザープロフィール
      updateData(
        () => store.dispatch('userProfile/updateUserProfile'),
        isInLoginSequence
      ),
      // ユーザーマスタデータ
      updateData(() => store.dispatch('user/updateUsers'), isInLoginSequence),
    ]

    if (isIndoorAvailable.value) {
      tasks.push(
        ...[
          updateData(
            () => store.dispatch('indoorSite/updateIndoorSitesWithThrottle'),
            isInLoginSequence
          ),
          updateData(
            () =>
              store.dispatch(
                'indoorBuilding/updateIndoorBuildingsWithThrottle'
              ),
            isInLoginSequence
          ),
          updateData(
            () => store.dispatch('indoorLevel/updateIndoorLevelsWithThrottle'),
            isInLoginSequence
          ),
        ]
      )
    }

    return Promise.all(tasks)
  }

  /**
   * ログインAPIの成功後に行う処理
   * 当メソッドで定義している処理までをログインのフローに含める
   * (失敗した場合はユーザーにログイン失敗として表示する)
   */
  const onAfterLoginSuccess = async () => {
    // ユーザーリスト表示設定の復元
    restoreListSettingFromLocalStorage()

    await Promise.all([
      // レイヤー表示状態引き継ぎ
      inheritLayerVisible(store.state.login.companyCode),
      // ログインユーザーの設定情報を取得し、storeに保存する
      getUserSetting(),
      // 新着メッセージ件数をリセットし、定期メッセージチェックを開始する
      store.dispatch('enableAppMessageCheck'),
      onLoadAtLoggedIn(true),
    ])

    store.commit('updateAppIsReady', true)
  }

  /**
   * ユーザー設定をサーバから取得する
   */
  const getUserSetting = async () => {
    const url = RestApiClient.createApiUrl('user_setting/myaccount')
    await updateData(async () => {
      const json = await RestApiClient.executeGetApi(url)
      store.dispatch('saveUserSetting', json.data)
    }, true)
  }

  onMounted(() => {
    // ログイン時にすぐ表示状態の引き継ぎができるように初期化しておく
    initInheritLayerVisibleClient()
  })

  return { onLoadAtLoggedIn, onAfterLoginSuccess }
}
